import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGoogleAuthenticate } from './useGoogleAuthenticate';
import type { GoogleAuthToken, GoogleAuthContextType, GoogleAuthProviderProps, GoogleConfig } from './types';
import { GoogleAuthError, GoogleService } from './types';

const GoogleAuthContext = createContext<GoogleAuthContextType | undefined>(undefined);

if (!GOOGLE_API_KEY || !GOOGLE_OAUTH_CLIENT_ID) {
  throw new Error('Google API credentials are not configured. Please contact support.');
}

const SERVICE_SCOPES: Record<GoogleService, string[]> = {
  [GoogleService.Picker]: ['https://www.googleapis.com/auth/drive.file'],
  [GoogleService.YouTube]: ['https://www.googleapis.com/auth/yt-analytics.readonly', 'https://www.googleapis.com/auth/youtube.readonly'],
};

const GOOGLE_CONFIG: GoogleConfig = {
  apiKey: GOOGLE_API_KEY,
  clientId: GOOGLE_OAUTH_CLIENT_ID,
  scopes: [...SERVICE_SCOPES[GoogleService.Picker], ...SERVICE_SCOPES[GoogleService.YouTube]],
  serviceScopes: SERVICE_SCOPES,
};

export const GoogleAuthProvider = ({ children }: GoogleAuthProviderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string>();
  const { t } = useTranslation(['common_terms']);

  const googleAuth = useGoogleAuthenticate();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await googleAuth.initialize(GOOGLE_CONFIG);
      } catch {
        throw new Error('Failed to initialize Google Auth');
      }
    };

    initializeAuth();
  }, []);

  const authorize = useCallback(
    async (service: GoogleService): Promise<void> => {
      setIsLoading(true);
      setAuthError(undefined);

      try {
        await googleAuth.authenticateWithPopup(service);
      } catch (err) {
        if (err instanceof GoogleAuthError && err.isUserFriendly) {
          setAuthError(err.message);
        } else {
          const errorMessage = t('authentication_failed_for');

          setAuthError(typeof errorMessage === 'string' ? errorMessage : undefined);
        }

        // Clear the token on error
        if (service) {
          googleAuth.clearToken(service);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [googleAuth, t]
  );

  const getServiceToken = useCallback(
    (service: GoogleService): GoogleAuthToken | undefined => {
      const token = googleAuth.getToken(service);

      return token || undefined;
    },
    [googleAuth]
  );

  const isAuthenticated = useCallback(
    (service: GoogleService): boolean => {
      return googleAuth.isAuthenticated(service);
    },
    [googleAuth]
  );

  const logout = useCallback(
    (service: GoogleService) => {
      googleAuth.clearToken(service);
      setAuthError(undefined);
    },
    [googleAuth]
  );

  return (
    <GoogleAuthContext.Provider
      value={{
        isAuthenticated,
        getServiceToken,
        isLoading,
        authError,
        setAuthError,
        authorize,
        logout,
      }}
    >
      {children}
    </GoogleAuthContext.Provider>
  );
};

export const useGoogleAuth = (): GoogleAuthContextType => {
  const context = useContext(GoogleAuthContext);

  if (context === undefined) {
    throw new Error('useGoogleAuth must be used within a GoogleAuthProvider');
  }

  return context;
};
